import { SaleOrderPdfVersion } from '../../../../shared/models/sale-order-pdf-version';
import { formatBudgetDateOnly } from '../../../pdf/pages/budget-preview/budget-preview.component';

export interface BudgetVersionGenerationError {
  message: string;
  /** True when the server may have stored a version, so the "Versiones" list must be reloaded. */
  refreshList: boolean;
}

const GENERIC_MESSAGE = 'No se pudo generar el presupuesto. Inténtalo de nuevo.';
const TIMEOUT_MESSAGE =
  'La generación tardó demasiado. Revisa la pestaña Versiones.';
const GENERATED_FALLBACK_MESSAGE = 'Presupuesto generado correctamente.';

/** Success toast for a generation: names the stored version when the 201 response carries its number. */
export function formatBudgetVersionGeneratedMessage(
  response: { data?: { versionNumber?: number | null } | null } | null | undefined
): string {
  const versionNumber = response?.data?.versionNumber;
  return typeof versionNumber === 'number' && Number.isInteger(versionNumber) && versionNumber > 0
    ? `Versión ${versionNumber} generada correctamente.`
    : GENERATED_FALLBACK_MESSAGE;
}

/** The "Generar presupuesto" button is offered only for an active budget that has quotation lines. */
export function canGenerateBudgetVersion(
  budget: { isActive?: boolean } | null | undefined,
  hasQuotationLines: boolean
): boolean {
  return !!budget && budget.isActive === true && hasQuotationLines;
}

/**
 * Builds the 422 message from the failure reason carried in `data`. `selectedIso` is the ISO the user
 * picked, used when the server does not echo `toCurrencyCode`.
 */
export function buildIssueCurrencyFailureMessage(
  data: Partial<SaleOrderPdfVersion> | null | undefined,
  selectedIso?: string | null
): string {
  const from = data?.fromCurrencyCode;
  const to = data?.toCurrencyCode || selectedIso;
  const noRate = () =>
    `No hay tasa de cambio de ${from} a ${to} para la fecha del documento (${formatBudgetDateOnly(data?.conversionDate)}). ` +
    `No se puede generar la versión en ${to}.`;

  switch (data?.issueCurrencyFailure) {
    case 'NoRate':
      return noRate();
    case 'NoBudgetCurrency':
      return `El presupuesto no tiene moneda; no se puede generar la versión en ${to}.`;
    case 'UnknownCurrency':
      return `La moneda ${to} ya no está disponible. No se puede generar la versión.`;
    default:
      return from && to ? noRate() : GENERIC_MESSAGE;
  }
}

/** True when a conversion was requested but the stored version carries no issue currency (old finance). */
export function wasIssueCurrencyIgnored(
  response: { data?: Partial<SaleOrderPdfVersion> | null } | null | undefined,
  conversionRequested: boolean
): boolean {
  return conversionRequested && !response?.data?.toCurrencyCode;
}

export function buildIssueCurrencyIgnoredMessage(): string {
  return 'La versión se generó en la moneda del presupuesto: el servidor no aplicó la moneda solicitada.';
}

/** Maps a failed generation request to the Spanish message shown to the user. */
export function mapBudgetVersionGenerationError(
  error: { status?: number; error?: { data?: Partial<SaleOrderPdfVersion> | null } | null } | null | undefined,
  selectedIso?: string | null
): BudgetVersionGenerationError {
  switch (error?.status) {
    case 422:
      return { message: buildIssueCurrencyFailureMessage(error.error?.data, selectedIso), refreshList: false };
    case 400:
      return {
        message: 'Solo se puede generar un presupuesto activo y con líneas.',
        refreshList: false,
      };
    case 404:
      return { message: 'No se encontró el presupuesto.', refreshList: false };
    case 409:
      return {
        message:
          'Hay otra generación en curso para este presupuesto. Inténtalo de nuevo en unos instantes.',
        refreshList: false,
      };
    case 0:
    case 504:
      return { message: TIMEOUT_MESSAGE, refreshList: true };
    case 500:
      return { message: GENERIC_MESSAGE, refreshList: true };
    default:
      return { message: GENERIC_MESSAGE, refreshList: false };
  }
}

export interface PastExpirationConfirmation {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
}

/**
 * Returns the expiration as `dd/MM/yyyy` when its calendar date (same rule as the budget page,
 * `formatBudgetDateOnly`) is before today's local date; null for today, a future date, or no valid date.
 */
export function getPastBudgetExpiration(
  expiration: string | Date | null | undefined,
  today: Date = new Date()
): string | null {
  const formatted = formatBudgetDateOnly(expiration);
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formatted);
  if (!match) return null;

  const [, day, month, year] = match;
  const expirationKey = Number(year) * 10000 + Number(month) * 100 + Number(day);
  const todayKey = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  return expirationKey < todayKey ? formatted : null;
}

/** Texts of the confirmation shown before generating a budget whose expiration already passed. */
export function buildPastExpirationConfirmation(formattedExpiration: string): PastExpirationConfirmation {
  return {
    title: 'El vencimiento ya pasó',
    content:
      `El vencimiento de este presupuesto (${formattedExpiration}) ya pasó. ` +
      '¿Quieres generarlo igualmente o editar el vencimiento antes?',
    okText: 'Generar igualmente',
    cancelText: 'Editar vencimiento',
  };
}
